import { ErrorModal } from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import moment, { type Moment } from "moment";
import { useEffect, useMemo, useState } from "react";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	useRFactorPerStandData,
	useRFactorPickers,
} from "../hooks/useRFactorPerStand";
import { useStands } from "@/shared/hooks/useStands";
import type { RFactorPerStandData } from "../models/RFactorPerStand";
import { RFactorPerStandChart } from "./RFactorPerStandChart";
import { RFactorPerStandSummary } from "./RFactorPerStandSummary";
import { RFactorPerStandToolbar } from "./RFactorPerStandToolbar";

const SQL_MIN_YEAR = 1753;

const isUsableDate = (date?: Moment | null): boolean =>
	!!date?.isValid() && date.year() >= SQL_MIN_YEAR;

// The legacy opens on today through today, and the pickers only offer what was
// rolled inside the range, so early in a production day both lists are empty.
const defaultDay = () => moment().startOf("day");

const URL_SCHEMA = {
	start: urlParam.date(defaultDay),
	end: urlParam.date(defaultDay),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
	crewId: urlParam.string(),
	standId: urlParam.int(),
};

const EMPTY_DATA: RFactorPerStandData = {
	points: [],
	hollows: [],
	shiftChanges: [],
};

export const RFactorPerStandReport = () => {
	const [
		{ start, end, productTypeId, productSizeId, crewId, standId },
		setFilters,
		{ resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const pickersQuery = useRFactorPickers(startStr, endStr);
	const standsQuery = useStands();
	const dataQuery = useRFactorPerStandData({
		start: startStr,
		end: endStr,
		productSizeId,
		standId,
		crewId,
	});

	const pickers = pickersQuery.data;
	const stands = useMemo(() => standsQuery.data ?? [], [standsQuery.data]);

	const productSizes = useMemo(() => {
		const all = pickers?.productSizes ?? [];
		if (productTypeId == null) return all;
		return all.filter((size) => size.ProductTypeId === productTypeId);
	}, [pickers, productTypeId]);

	// The product is chosen, never chosen for you. Picking the first of the list
	// when the range changes is worse than showing nothing: the chart quietly
	// becomes a different product while the operator is reading it. A selection
	// the new range no longer offers is dropped and said so, not substituted.
	//
	// The legacy has no All option here either, and cannot: the procedure filters
	// on ProductSizeId equality, and a line mixing products would be one recipe
	// crossing into another rather than a reading of anything.
	// Guarded on the pickers having answered: before that the list is empty and every
	// product, including one restored from the URL, would look invalid.
	useEffect(() => {
		if (!pickersQuery.isSuccess || productSizeId == null) return;
		if (!productSizes.some((s) => s.Id === productSizeId))
			setFilters({ productSizeId: null });
	}, [pickersQuery.isSuccess, productSizes, productSizeId, setFilters]);

	// The legacy shows H1 while holding 0 in its model, so unless the operator
	// touches the dropdown the query asks for a stand that does not exist and the
	// chart stays empty. Here the control and the query agree from the start.
	useEffect(() => {
		if (!stands.length) return;
		if (standId != null && stands.some((s) => s.Id === standId)) return;
		setFilters({ standId: stands[0].Id });
	}, [stands, standId, setFilters]);

	useEffect(() => {
		if (crewId && pickers && !pickers.crews.includes(crewId))
			setFilters({ crewId: null });
	}, [pickers, crewId, setFilters]);

	useEffect(() => {
		const error = (dataQuery.error ??
			pickersQuery.error ??
			standsQuery.error) as Error | null;
		if (error) setErrorMsg(error.message);
	}, [dataQuery.error, pickersQuery.error, standsQuery.error]);

	// Three ways for the chart to be blank, and they look identical.
	const emptyReason = !productSizes.length
		? "No product was rolled in this range"
		: productSizeId == null
			? "Select a product to draw the chart"
			: "No data available";

	const isLoading =
		pickersQuery.isLoading ||
		standsQuery.isLoading ||
		(productSizeId != null && standId != null && dataQuery.isLoading);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				R-Factor per Stand
			</Typography>

			<Paper elevation={3}>
				<RFactorPerStandToolbar
					start={start}
					end={end}
					productTypeId={productTypeId}
					productSizeId={productSizeId}
					crewId={crewId}
					standId={standId}
					productTypes={pickers?.productTypes ?? []}
					productSizes={productSizes}
					crews={pickers?.crews ?? []}
					onStartChange={(d) =>
						isUsableDate(d) && d && setFilters({ start: d })
					}
					onEndChange={(d) => isUsableDate(d) && d && setFilters({ end: d })}
					onProductTypeChange={(id) => setFilters({ productTypeId: id })}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					onCrewChange={(value) => setFilters({ crewId: value })}
					onStandChange={(id) => setFilters({ standId: id })}
					onReset={resetValues}
					canReset={
						!isDefault(
							"start",
							"end",
							"productTypeId",
							"productSizeId",
							"crewId",
						) || standId !== stands[0]?.Id
					}
				/>
			</Paper>

			<RFactorPerStandSummary data={dataQuery.data ?? EMPTY_DATA} />

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden", minHeight: 300 }}>
				{isLoading ? (
					<CenteredLazyLoading />
				) : (
					<RFactorPerStandChart
						data={dataQuery.data ?? EMPTY_DATA}
						emptyReason={emptyReason}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
